"use client";

import * as React from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { debounce } from "es-toolkit";

export interface Country {
  id: number;
  name: string;
  iso2: string;
}

interface CountrySelectProps {
  value?: string | Country;
  disabled?: boolean;
  onValueChange?: (value: Country | undefined) => void;
  placeholder?: string;
  apiEndpoint: string;
  className?: string;
}

// Spostato fuori o memorizzato per evitare re-render inutili
const CountryLabel = ({ country }: { country: Country }) => (
  <span className="flex items-center gap-2">
    <span className={`fi fi-${country.iso2?.toLowerCase()} rounded-xs`}></span>
    {country.name}
  </span>
);

export function CountrySelect({
  value,
  disabled,
  onValueChange,
  placeholder = "Select country...",
  apiEndpoint,
  className,
}: CountrySelectProps) {
  const [open, setOpen] = React.useState(false);
  const [countries, setCountries] = React.useState<Country[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");

  // FIXED: Stato per memorizzare il paese selezionato
  // (evita che sparisca quando la lista API cambia)
  const [selectedCountry, setSelectedCountry] = React.useState<
    Country | undefined
  >();

  // Debounce corretto con useMemo
  const debouncedSearch = React.useMemo(
    () => debounce((val: string) => setSearch(val), 500),
    [],
  );

  // Effect A: Fetch paesi (dipende solo da search e apiEndpoint)
  React.useEffect(() => {
    let cancelled = false;

    const fetchCountries = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.append("search", search);

        const response = await fetch(`${apiEndpoint}?${params.toString()}`);
        const countryList: Country[] = await response.json();
        if (!cancelled) {
          setCountries(countryList);
        }
      } catch (error) {
        // silently handle
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCountries();
    return () => {
      cancelled = true;
    };
  }, [search, apiEndpoint]);

  // Effect B: Sync selectedCountry con il value prop
  React.useEffect(() => {
    if (!value) {
      setSelectedCountry(undefined);
      return;
    }

    if (typeof value === "object" && value.name && value.id && value.id !== 0) {
      setSelectedCountry(value);
    } else {
      const iso2 = (
        typeof value === "string" ? value : value.iso2
      )?.toLowerCase();
      if (iso2) {
        const found = countries.find((c) => c.iso2.toLowerCase() === iso2);
        if (found) {
          setSelectedCountry(found);
          if (typeof value === "string" || !value.id || value.id === 0 || !value.name) {
            onValueChange?.(found);
          }
          return;
        }
        // Non nella lista corrente — fetch specifico
        const fetchSpecific = async () => {
          try {
            const params = new URLSearchParams();
            params.append("iso2", iso2);
            const response = await fetch(`${apiEndpoint}?${params.toString()}`);
            const result: Country[] = await response.json();
            const match = result.find(
              (c: Country) => c.iso2.toLowerCase() === iso2,
            );
            if (match) {
              setSelectedCountry(match);
              onValueChange?.(match);
            }
          } catch {
            // silently handle
          }
        };
        fetchSpecific();
      }
    }
  }, [value, countries, apiEndpoint]);

  return (
    <Popover
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          debouncedSearch.cancel();
          setSearch("");
        }
        setOpen(newOpen);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          role="combobox"
          className={cn("w-full justify-between bg-transparent", className)}
        >
          {selectedCountry ? (
            <CountryLabel country={selectedCountry} />
          ) : (
            placeholder
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="min-w-[300px] p-0 max-w-[400px]">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search country..."
            onValueChange={debouncedSearch}
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  {countries.map((country) => (
                    <CommandItem
                      key={country.id}
                      value={country.iso2?.toLowerCase()} // Changed to use ISO2
                      onSelect={() => {
                        setSelectedCountry(country);
                        onValueChange?.(country);
                        debouncedSearch.cancel();
                        setSearch("");
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          (typeof value === "string" &&
                            value.toLowerCase() ===
                              country.iso2.toLowerCase()) ||
                            (typeof value === "object" &&
                              value?.id === country.id) ||
                            selectedCountry?.id === country.id
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      <CountryLabel country={country} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
